import './CurrentKnowledgeBasePage.css';
import React, { Fragment } from 'react';
import { clsx } from 'clsx';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import CardContainer from 'src/components/CardContainer/CardContainer';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { useParams } from 'react-router-dom';
import { KNOWLEDGE_BASE_FILE_EXTENSIONS } from 'src/shared/constants';
import { CurrentKnowledgeBaseTableList } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/CurrentKnowledgeBaseTableList';
import { Button } from 'src/components/ui/button';
import {
  DeleteItemsKnowledgeBaseIcon,
  MoveItemIcon,
  MoveItemsKnowledgeBaseIcon,
} from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseIcons/CurrentKnowledgeBaseIcons';
import { SearchInput } from 'src/components/SearchInput/SearchInput';
import { AddKnowledgeBaseSourceModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/AddKnowledgeBaseSourceModal/AddKnowledgeBaseSourceModal';
import { AddKnowledgeBaseFolderModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/create/AddKnowledgeBaseFolderModal/AddKnowledgeBaseFolderModal';
import { FiltersKnowledgeBaseModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/FiltersKnowledgeBaseModal/FiltersKnowledgeBaseModal';
import { DeleteKnowledgeBaseItemsModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/delete/DeleteKnowledgeBaseItemsModal/DeleteKnowledgeBaseItemsModal';
import { MoveKnowledgeBaseItemModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/MoveKnowledgeBaseItemModal/MoveKnowledgeBaseItemModal';
import { ExportKnowledgeBaseModal } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/ExportKnowledgeBaseModal/ExportKnowledgeBaseModal';
import { CurrentKnowledgeBaseBreadcrumb } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseBreadcrumb/CurrentKnowledgeBaseBreadcrumb';
import { SelectAllCheckboxKnowledgeBasePage } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseTableList/SelectAllCheckboxKnowledgeBase';
import { KnowledgeBaseFiltersList } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/KnowledgeBaseFiltersList/KnowledgeBaseFiltersList';
import { useKBContext } from 'src/provider/KnowledgeBaseProvider';
import { CurrentKnowledgeBaseEditFilePopup } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/components/CurrentKnowledgeBaseEditFilePopup/CurrentKnowledgeBaseEditFilePopup';
import { Separator } from 'src/components/ui/separator';
import { useKnowledgeBaseData } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseData';
import { useKnowledgeBaseTree } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseTree';
import { useKnowledgeBaseSelection } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseSelection';
import { useKnowledgeBaseFileActions } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseFileActions';
import { useKnowledgeBaseExport } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/hooks/useKnowledgeBaseExport';

export const CurrentKnowledgeBasePage = () => {
  const {
    tree,
    setTree,
    activeBranch,
    updateActiveBranch,
    setFilteredValue,
    setKnowledgeBaseName,
    knowledgeBaseName,
    skipRefetchFiles,
    setSkipRefetchFiles,
  } = useKBContext();
  const isLgDown = useMediaQuery('(max-width: 992px)');
  const { id: knowledgeBaseId } = useParams();

  const {
    currentKnowledgeBase,
    files,
    isLoadingFiles,
    isSuccessFiles,
    isActionButtonsDisabled,
    isCosupportManager,
    isV1,
    refetchFilesAndSync,
    getLazyKnowledgeBaseFiles,
    fetchFileById,
    addKnowledgeBaseFile,
    addKnowledgeBaseDocument,
    deleteKnowledgeBaseFiles,
    updateKnowledgeBaseFile,
    updateKnowledgeBaseFiles,
    integrations,
  } = useKnowledgeBaseData(knowledgeBaseId, skipRefetchFiles);

  const { onFileSearch } = useKnowledgeBaseTree({
    knowledgeBaseId,
    tree,
    setTree,
    activeBranch,
    updateActiveBranch,
    setFilteredValue,
    setKnowledgeBaseName,
    files,
    integrations,
    currentKnowledgeBase,
    skipRefetchFiles,
    isSuccessFiles,
    isV1,
    getLazyKnowledgeBaseFiles,
  });

  const {
    moveContentMode,
    deleteContentMode,
    checkedItemsCount,
    checkedFilesCount,
    onCheckedChange,
    handleSelectAllChange,
    resetCheckedItems,
    cancelCheckboxMode,
    setMoveContentMode,
    setDeleteContentMode,
  } = useKnowledgeBaseSelection({ tree, activeBranch, updateActiveBranch });

  const {
    dragStarted,
    onDragEnter,
    onDragOver,
    onDragLeave,
    handleDrop,
    onFileChange,
    fileInputValue,
    inputFileRef,
    handleAddFolderItem,
    addKnowledgeBaseSource,
    onAddKnowledgeBaseSourceOpen,
    handleItemsDelete,
    handleMoveItems,
  } = useKnowledgeBaseFileActions({
    knowledgeBaseId,
    tree,
    activeBranch,
    updateActiveBranch,
    skipRefetchFiles,
    setSkipRefetchFiles,
    addKnowledgeBaseFile,
    addKnowledgeBaseDocument,
    deleteKnowledgeBaseFiles,
    updateKnowledgeBaseFile,
    updateKnowledgeBaseFiles,
    refetchFilesAndSync,
    cancelCheckboxMode,
    resetCheckedItems,
  });

  const { exportAllFiles } = useKnowledgeBaseExport({
    files,
    knowledgeBaseName: currentKnowledgeBase?.name,
    fetchFileById,
  });

  return (
    <Fragment>
      <PageContentWrapper
        className={clsx('knowledge-base-page-container', !isLgDown && 'relative')}
        isMobileMarginTop={false}
        isProjectBreadcrumbs={true}
        projectBreadcrumbsContent={knowledgeBaseName}
      >
        <CurrentKnowledgeBaseEditFilePopup />
        <div className="z-10 gap-[10px] flex flex-col relative h-[100%]">
          <div className={'flex flex-row justify-between'}>
            <CurrentKnowledgeBaseBreadcrumb />
            <div className={'relative flex flex-row gap-2'}>
              {isCosupportManager && isV1 && (
                <ExportKnowledgeBaseModal
                  className={clsx(Boolean(moveContentMode || deleteContentMode) && 'hidden')}
                  handleExport={exportAllFiles}
                />
              )}
            </div>
          </div>
          <CardContainer className="h-[100%] min-w-[500px]">
            <div className={'flex flex-row justify-between gap-32'}>
              <div className={'flex flex-row gap-2.5'}>
                <AddKnowledgeBaseFolderModal
                  handleAddFolderItem={handleAddFolderItem}
                  disabled={moveContentMode || deleteContentMode}
                />
                <AddKnowledgeBaseSourceModal
                  addKnowledgeBaseSource={addKnowledgeBaseSource}
                  disabled={moveContentMode || deleteContentMode}
                  onAddKnowledgeBaseSourceOpen={onAddKnowledgeBaseSourceOpen}
                />
                <FiltersKnowledgeBaseModal />
                <KnowledgeBaseFiltersList />
                {(moveContentMode || deleteContentMode) && (
                  <div className={'flex flex-row gap-2.5'}>
                    <div
                      className={
                        'text-xs font-medium text-ebony_opacity_30 flex flex-row gap-2.5 items-center justify-center'
                      }
                    >
                      <div
                        className={
                          'px-1.5 py-0.5 bg-ebony text-white rounded-sm-5 min-w-6 flex items-center justify-center'
                        }
                      >
                        {checkedFilesCount}
                      </div>
                      <div>Selected</div>
                    </div>
                    <div
                      className={
                        'text-xs font-medium text-ebony_opacity_30 flex flex-row gap-2.5 items-center justify-center'
                      }
                    >
                      <SelectAllCheckboxKnowledgeBasePage onChange={handleSelectAllChange} />
                    </div>
                  </div>
                )}
              </div>
              {isActionButtonsDisabled ? (
                <div className={'flex flex-row items-center'}>
                  <p className={'text-xs text-center text-ebony_opacity_50 font-medium'}>
                    Hold on a moment. We’re updating your Knowledge Base.
                    <br /> Bulk actions will be back as soon as the update is done.
                  </p>
                </div>
              ) : (
                <div className={'flex flex-1 flex-row gap-2.5 relative justify-end'}>
                  <SearchInput
                    className={clsx(
                      '!min-h-[34px] right-[88px]',
                      (moveContentMode || deleteContentMode) && 'hidden',
                    )}
                    inputClassName={'!max-h-[34px]'}
                    onChange={onFileSearch}
                    visibleClassName={' w-[calc(100%-100px)]'}
                    disabled={isActionButtonsDisabled}
                  />
                  {moveContentMode && (
                    <Button
                      variant="outline"
                      type="button"
                      className="min-w-24 flex flex-fow gap-2 text-ebony_opacity_80 text-base-xs"
                      onClick={cancelCheckboxMode}
                      disabled={isActionButtonsDisabled}
                    >
                      <span>Cancel</span>
                    </Button>
                  )}
                  {moveContentMode ? (
                    <MoveKnowledgeBaseItemModal
                      trigger={
                        <Button className="min-w-24 gap-2">
                          Move <MoveItemIcon color={'#FFFFFF'} />
                        </Button>
                      }
                      item={activeBranch}
                      handleMoveFiles={handleMoveItems}
                      disabled={!checkedItemsCount || isActionButtonsDisabled}
                    />
                  ) : (
                    <Button
                      variant="outline"
                      type="button"
                      className={clsx('px-1 w-8.5', deleteContentMode && 'hidden')}
                      onClick={() => {
                        setMoveContentMode(true);
                      }}
                      disabled={deleteContentMode || isActionButtonsDisabled}
                    >
                      <MoveItemsKnowledgeBaseIcon />
                    </Button>
                  )}

                  {deleteContentMode && (
                    <Button
                      variant="outline"
                      type="button"
                      className="min-w-24 flex flex-fow gap-2 text-ebony_opacity_80 text-base-xs"
                      onClick={cancelCheckboxMode}
                      disabled={isActionButtonsDisabled}
                    >
                      <span>Cancel</span>
                    </Button>
                  )}
                  {deleteContentMode ? (
                    <DeleteKnowledgeBaseItemsModal
                      handleItemsDelete={handleItemsDelete}
                      disabled={!checkedItemsCount || isActionButtonsDisabled}
                    />
                  ) : (
                    <Button
                      variant="outline"
                      type="button"
                      className={clsx('px-2 w-8.5', moveContentMode && 'hidden')}
                      onClick={() => {
                        setDeleteContentMode(true);
                      }}
                      disabled={moveContentMode || isActionButtonsDisabled}
                    >
                      <DeleteItemsKnowledgeBaseIcon />
                    </Button>
                  )}
                </div>
              )}
            </div>
            <Separator className={'bg-light mt-2'} />

            <section
              className={clsx(dragStarted && 'drag-active', 'overflow-hidden')}
              onDrop={handleDrop}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
            >
              <input
                type="file"
                ref={inputFileRef}
                className={'hidden'}
                onChange={onFileChange}
                multiple={true}
                accept={KNOWLEDGE_BASE_FILE_EXTENSIONS.filter((ext) => ext !== '.faq').join(', ')}
                value={fileInputValue}
              />
              {activeBranch && (
                <CurrentKnowledgeBaseTableList
                  className={clsx('h-[60vh] overflow-auto view-type-list')}
                  onCheckedChange={onCheckedChange}
                  handleItemsDelete={handleItemsDelete}
                  handleMoveItems={handleMoveItems}
                  isLoadingFiles={isLoadingFiles}
                  deleteContentMode={deleteContentMode}
                  moveContentMode={moveContentMode}
                />
              )}
            </section>
          </CardContainer>
        </div>
      </PageContentWrapper>
    </Fragment>
  );
};
